import { createFilterOptions } from '@mui/material/Autocomplete'
import map from 'lodash/map'

import { Tag } from 'types/Participants'
import { AutocompleteOptions } from 'types/Genric'
import { MAX_PARTICIPANT_TAGS_CHAR } from 'lib/GlobalConstants'
import i18n from 'i18n'
import { checkObjectProperty, triggeredQuestions } from 'lib/Helper'

export interface ParticipantTagsValue {
  id: string
  type: string
  name: string
  createdAt: string
  taggingsCount: number
}

const filter = createFilterOptions()

export const createTagsOptions = (
  options: Tag[],
  params: AutocompleteOptions,
  createLocale: string,
  selectedTags?: any,
) => {
  const filtered: any = filter(options, params)
  const { inputValue } = params
  // checking in dropdown
  const isExisting = options.some((option: Tag) => inputValue === option.name)

  /**
    This function checks if a given tag 
    is already present, and if it is, it does 
    not create a new option with that tag name. 
    The reason for returning false if the selected 
    tag is not present is to make sure the condition passes when 
    the component using the function does not send the selected tags as 
    a parameter.
   */
  const isTagAdded: boolean = selectedTags?.length
    ? selectedTags.some((tag: { name: string }) => tag.name === inputValue)
    : false

  const isTagUnique = inputValue !== '' && !isExisting && !isTagAdded

  if (isTagUnique) {
    filtered.push({
      inputValue,
      name: `${createLocale} "${inputValue}"`,
    })
  }

  return filtered
}

export const validateParticiapantTags = (
  value: string,
  setInputValue: (args0: string) => void,
) => {
  const regExp = /^[a-z0-9&_\-']*$/gim
  if (regExp.test(value) && value.length <= MAX_PARTICIPANT_TAGS_CHAR) {
    setInputValue(value)
  }
}

export const isTagAlreadySelected = (
  participantTagsValue: ParticipantTagsValue[],
  requestTag: string,
) => {
  const alreadySelectedTags = map(participantTagsValue, 'name')
  return alreadySelectedTags.includes(requestTag)
}

export const getDefaultErrorMessage = (
  signupFormAnswers: IndexType,
  setMessage: (arg0: string) => void,
  id: string,
) => {
  const blanks = signupFormAnswers?.blankQuestions

  if (!blanks && blanks.length === 0) {
    setMessage('')
    return
  }

  for (let i = 0; i < blanks.length; i++) {
    const invalidQuestion = blanks[i]

    if (invalidQuestion.id === id) {
      setMessage(invalidQuestion.message)
      return
    }
  }

  setMessage('')
  return
}

export const getDefaultErrorState = (
  signupFormAnswers: IndexType,
  setError: (arg0: boolean) => void,
  id: string,
) => {
  const blanks = signupFormAnswers?.blankQuestions

  if (!blanks && blanks.length === 0) {
    setError(false)
    return
  }

  for (let i = 0; i < blanks.length; i++) {
    const invalidQuestion = blanks[i]

    if (invalidQuestion.id === id) {
      setError(true)
      return
    }
  }

  setError(false)

  return
}

// temporary duplicate function  similar to
// https://github.com/bangthetable/www-admin-ui/blob/0bc8e6a24fffce5f83135d02d0db7228d2d3b6c2/src/components/SignupFormQuestions/EmailQuestion/index.tsx#L35
export const validateEmailQuestionOnSubmit = (answer: string) => {
  // only chars after @ symbol [ -, _ and .]

  const onlyCharsAfter = /@[a-zA-Z0-9_\-.]+$/gim.test(answer)

  // only chars before .%+'-_&
  const onlyCharsBefore = /^[a-zA-Z0-9.%+'\-_&]+@/gim.test(answer)

  // chars after .

  const charsAfterDot = /@\w+\.\w{2,}$/gim.test(answer)

  switch (true) {
    case answer && onlyCharsAfter && onlyCharsBefore && charsAfterDot:
      return ''
    case !onlyCharsBefore:
      return 'special_chars_before'
    case !onlyCharsAfter:
      return 'special_chars'
    case !charsAfterDot:
      return 'chars_after_dot'
  }
}

type Error = string | string[]

export const getErrorHelperText = (error: Error, question: string) => {
  if (error[0] === 'blank') {
    return i18n.t('errors.blank', { question })
  }

  if (error === 'taken' || error === 'notAnEmail') {
    return i18n.t(`errors.${error}`, { question })
  }

  return error
}

export const getTriggerQuestions = (
  questionsInPage: IndexType[],
  question: { id: string | number },
  conditionalQuestions: IndexType,
  answers: IndexType,
) => {
  const isTriggerQuestion = checkObjectProperty(
    conditionalQuestions,
    question.id,
  )

  if (!isTriggerQuestion) {
    return
  }

  const triggerQuestions = triggeredQuestions(
    question,
    answers[question.id],
    conditionalQuestions[question.id],
  )

  for (let i = 0; i < triggerQuestions.length; i++) {
    const triggeredQuestion = triggerQuestions[i]
    questionsInPage.push(triggeredQuestion)
    getTriggerQuestions(
      questionsInPage,
      triggeredQuestion,
      conditionalQuestions,
      answers,
    )
  }
}

export const blockInvalidChar = (e: {
  key: string
  preventDefault: () => void
}) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
